/**
 * Page Header Component
 * Consistent header layout for list and detail pages
 */

import React from "react";
import { Box, Typography, Button, Breadcrumbs, Link, Tooltip } from "@mui/material";
import { ArrowBack as BackIcon, Add as AddIcon } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { HelpButton } from "./HelpButton";

export interface BreadcrumbItem {
  label: string;
  href?: string;
}

export interface PageHeaderAction {
  label: string;
  icon?: React.ReactNode;
  onClick: () => void;
  variant?: "text" | "outlined" | "contained";
  color?: "primary" | "secondary" | "error" | "warning" | "info" | "success";
  disabled?: boolean;
  /**
   * Why the action can't be used right now. Shown on hover, focus and touch;
   * pair it with `disabled` so the person who sees the action also learns
   * what to do instead.
   */
  tooltip?: string;
}

export interface PageHeaderProps {
  /** Page title */
  title: string;
  /** Subtitle or description */
  subtitle?: string;
  /** Breadcrumb navigation items */
  breadcrumbs?: BreadcrumbItem[];
  /** Primary action button */
  primaryAction?: PageHeaderAction;
  /** Secondary actions */
  secondaryActions?: PageHeaderAction[];
  /** Back button URL (shows back button if provided) */
  backUrl?: string;
  /** Shown under the title and subtitle, e.g. status chips. Unlike children,
   *  it stays with the title when the actions wrap to their own line. */
  titleAdornment?: React.ReactNode;
  /** Children to render (e.g., tabs) */
  children?: React.ReactNode;
  /** Help guide slug for contextual help button */
  helpSlug?: string;
}

/**
 * One header button. With a `tooltip` on a disabled action, the button stays
 * focusable and is marked aria-disabled instead of `disabled` (a disabled
 * button gets no focus or pointer events, so its reason could never be read),
 * and the tooltip describes the button (describeChild) rather than renaming it:
 * a screen reader hears "Void, dimmed, <reason>".
 */
const HeaderActionButton: React.FC<{
  action: PageHeaderAction;
  defaultVariant: NonNullable<PageHeaderAction["variant"]>;
  defaultIcon?: React.ReactNode;
}> = ({ action, defaultVariant, defaultIcon }) => {
  const explainDisabled = !!action.disabled && !!action.tooltip;
  const variant = action.variant || defaultVariant;
  const filled = variant === "contained";
  const button = (
    <Button
      variant={variant}
      color={action.color || "primary"}
      startIcon={action.icon || defaultIcon}
      onClick={explainDisabled ? undefined : action.onClick}
      disabled={action.disabled && !explainDisabled}
      aria-disabled={explainDisabled || undefined}
      disableRipple={explainDisabled}
      sx={
        explainDisabled
          ? {
              color: "text.disabled",
              borderColor: "action.disabledBackground",
              bgcolor: filled ? "action.disabledBackground" : undefined,
              boxShadow: "none",
              cursor: "not-allowed",
              "&:hover": {
                bgcolor: filled ? "action.disabledBackground" : "transparent",
                borderColor: "action.disabledBackground",
                boxShadow: "none",
              },
            }
          : undefined
      }
    >
      {action.label}
    </Button>
  );
  if (!action.tooltip) return button;
  return (
    <Tooltip title={action.tooltip} describeChild enterTouchDelay={0}>
      {button}
    </Tooltip>
  );
};

export const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  subtitle,
  breadcrumbs,
  primaryAction,
  secondaryActions,
  backUrl,
  titleAdornment,
  children,
  helpSlug,
}) => {
  const navigate = useNavigate();

  return (
    <Box sx={{ mb: 3 }}>
      {/* Breadcrumbs */}
      {breadcrumbs && breadcrumbs.length > 0 && (
        <Breadcrumbs sx={{ mb: 1 }}>
          {breadcrumbs.map((item, index) => (
            item.href ? (
              <Link
                key={index}
                color="inherit"
                href={item.href}
                onClick={(e) => {
                  e.preventDefault();
                  navigate(item.href!);
                }}
                sx={{ cursor: "pointer", textDecoration: "none", "&:hover": { textDecoration: "underline" } }}
              >
                {item.label}
              </Link>
            ) : (
              <Typography key={index} sx={{
                color: "text.primary"
              }}>
                {item.label}
              </Typography>
            )
          ))}
        </Breadcrumbs>
      )}
      {/* Header Row. It wraps: when the title and the actions don't fit side
          by side (several actions, or the desktop sidebar taking width), the
          actions move under the title instead of squeezing the title to one
          word per line. */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          flexWrap: { sm: "wrap" },
          justifyContent: "space-between",
          alignItems: { xs: "stretch", sm: "flex-start" },
          gap: 2,
        }}
      >
        {/* Left side - Title */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, minWidth: 0 }}>
          {backUrl && (
            <Button
              variant="text"
              size="small"
              startIcon={<BackIcon />}
              onClick={() => navigate(backUrl)}
              sx={{ minWidth: "auto", px: 1 }}
            >
              Back
            </Button>
          )}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box>
              <Typography variant="h4" component="h1" sx={{
                fontWeight: 700
              }}>
                {title}
              </Typography>
              {subtitle && (
                <Typography
                  variant="body2"
                  sx={{
                    color: "text.secondary",
                    mt: 0.5
                  }}>
                  {subtitle}
                </Typography>
              )}
              {titleAdornment}
            </Box>
            {helpSlug && <HelpButton slug={helpSlug} />}
          </Box>
        </Box>

        {/* Right side - Actions */}
        {(primaryAction || (secondaryActions && secondaryActions.length > 0)) && (
          // flexWrap so a second action drops to its own line rather than
          // widening the page past a narrow viewport (WCAG 1.4.10 reflow).
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            {secondaryActions?.map((action, index) => (
              <HeaderActionButton
                key={index}
                action={action}
                defaultVariant="outlined"
              />
            ))}
            {primaryAction && (
              <HeaderActionButton
                action={primaryAction}
                defaultVariant="contained"
                defaultIcon={<AddIcon />}
              />
            )}
          </Box>
        )}
      </Box>
      {/* Optional children (tabs, etc.) */}
      {children}
    </Box>
  );
};

export default PageHeader;
